import { ReactNode, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useUpdateCompanyByIdMutation } from "../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../Hooks/hooks";
import useDialogHooks from "../../../../../../../Components/Dialog/dialog-hooks";
import { IProfilecontactItem } from "../../../../../../../Interfaces/componentTypes";
import { IEditBusinessProfileMainProps } from "../ProfileMain/profileMainInfo";
import ProfileContactsDialog from "../../../Dialogs/ProfileContactsDialog/profileContactsDialog";
import { IProfileContactsForm } from "../../../../../../../Interfaces/formValue-interfaces";
import { IContacts } from "../../../../../../../Interfaces/responseTypes";
import { IUpdateCompanyById } from "../../../../../../../Interfaces/request-interfaces";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";
import { fullProfileContact } from "../../../../../../../Costants/costants";

const useProfileContacts = ({ data, onUpdate }: IEditBusinessProfileMainProps) => {
    const { open, onOpen, onClose } = useDialogHooks();
    const { t } = useLanguagesHooks();
    const [update, { data: updatedData, isSuccess: isUpdated, isError: isUpdateError, isLoading }] = useUpdateCompanyByIdMutation();
    const [dialogComponent, setDialogComponent] = useState<ReactNode | null>(null);
    const { contacts } = data;
    const [isChanged, setIsChanged] = useState(false);
    const isInitializing = useRef(true);
    const { showToastError, showToastSuccess } = useToastify();
    const scrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const {
        register,
        control,
        setValue,
        getValues,
        trigger,
        watch,
    } = useForm<IProfileContactsForm>({
        mode: "all",
        defaultValues: {
            phone_numbers: [{ id: "", value: "" }],
        },
    });

    const [confirmedContacts, setConfirmedContacts] = useState<IProfileContactsForm>({
        phone_numbers: [],
        facebook: "",
        instagram: "",
        telegram: "",
        whatsapp: "",
        viber: "",
    });

    useEffect(() => {
        if (contacts) {
            const initial: IProfileContactsForm = {
                phone_numbers: [],
                facebook: "",
                instagram: "",
                telegram: "",
                whatsapp: "",
                viber: "",
            };
            const phones = (contacts.phone_numbers ?? []).map((phone, index) => ({
                id: `${index}-${phone}`,
                value: phone,
            }));

            setValue("phone_numbers", phones);
            initial.phone_numbers = phones;

            (["facebook", "instagram", "telegram", "whatsapp", "viber"] as const).forEach((key) => {
                if (contacts[key]) {
                    setValue(key, contacts[key]!);
                    initial[key] = contacts[key]!;
                }
            });

            setConfirmedContacts(initial);
            setTimeout(() => {
                isInitializing.current = false;
            }, 0);
        }
    }, [contacts, setValue]);

    const normalizePhones = (phones: typeof confirmedContacts.phone_numbers) =>
        (phones ?? [])
            .map((p) => p?.value?.trim())
            .filter((p) => p);

    const checkForChanges = (formData: IProfileContactsForm): boolean => {
        const currentPhones = normalizePhones(formData.phone_numbers);
        const confirmedPhones = normalizePhones(confirmedContacts.phone_numbers);

        const phoneChanged = JSON.stringify(currentPhones) !== JSON.stringify(confirmedPhones);

        const otherChanged = (["facebook", "instagram", "telegram", "whatsapp", "viber"] as const).some((key) =>
            (formData[key] ?? "").trim() !== (confirmedContacts[key] ?? "").trim()
        );

        return phoneChanged || otherChanged;
    };

    const handleConfirmContact = (id: keyof IProfileContactsForm, value: any) => {
        setValue(id, value);

        const updatedForm: IProfileContactsForm = {
            ...getValues(),
            [id]: value,
        };

        const changed = checkForChanges(updatedForm);

        setConfirmedContacts((prev) => ({ ...prev, [id]: value }));
        setIsChanged(changed);

        onCloseContactDialog();
    };

    const handleDeleteContact = (id: keyof IContacts) => {
        let updatedForm: IProfileContactsForm;

        if (id === "phone_numbers") {
            const emptyPhones = [{ id: "", value: "" }];
            setValue("phone_numbers", emptyPhones);
            updatedForm = {
                ...getValues(),
                phone_numbers: [],
            };
            setConfirmedContacts((prev) => ({
                ...prev,
                phone_numbers: [],
            }));
        } else {
            setValue(id, "");
            updatedForm = {
                ...getValues(),
                [id]: "",
            };
            setConfirmedContacts((prev) => ({
                ...prev,
                [id]: "",
            }));
        }

        const changed = checkForChanges(updatedForm);

        if (!changed) {
            setIsChanged(false);
        } else {
            setIsChanged(changed);
        }
    };

    const filledContacts = fullProfileContact
        .map((field) => {
            const val = confirmedContacts[field.id as keyof IProfileContactsForm];
            if (field.id === "phone_numbers" && Array.isArray(val) && val.length > 0) {
                return {
                    id: field.id,
                    value: val.map((p) => p.value).join(", "),
                    icon: field.icon,
                };
            } else if (typeof val === "string" && val.trim()) {
                return {
                    id: field.id,
                    value: val,
                    icon: field.icon,
                };
            }
            return null;
        })
        .filter(Boolean);

    const onOpenContactDialog = (data: IProfilecontactItem) => {
        const val = confirmedContacts[data.id as keyof IProfileContactsForm];

        if (data.id === "phone_numbers") {
            const safeVal = Array.isArray(val) && val.length ? val : [{ id: "", value: "" }];
            setValue("phone_numbers", safeVal);
        } else {
            setValue(data.id, typeof val === "string" ? val : "");
        }

        setDialogComponent(
            <ProfileContactsDialog
                data={data}
                control={control}
                watch={watch}
                getValues={getValues}
                register={register}
                trigger={trigger}
                onClose={onCloseContactDialog}
                onConfirm={handleConfirmContact}
            />
        );
        onOpen();
    };

    const onCloseContactDialog = () => {
        setDialogComponent(null);
        scrollTop();
        onClose();
    };

    const profileContacts = fullProfileContact.filter((item) => {
        if (item.id === "phone_numbers") return true;
        const value = confirmedContacts[item.id as keyof IProfileContactsForm];
        return value === "" || !value;
    });

    useEffect(() => {
        if (isUpdated && updatedData) {
            onUpdate();
            showToastSuccess(t('Toast.Success.Common'));
            setIsChanged(false);
            scrollTop();
        } else if (isUpdateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [isUpdated, updatedData, isUpdateError]);

    const handleSubmitContacts = () => {
        const payload: IUpdateCompanyById = {
            id: data.id,
            data: {
                contacts: {
                    phone_numbers: confirmedContacts.phone_numbers
                        ?.map((item) => item.value.trim())
                        .filter((val) => val !== ""),
                    facebook: confirmedContacts.facebook?.trim() || null,
                    instagram: confirmedContacts.instagram?.trim() || null,
                    telegram: confirmedContacts.telegram?.trim() || null,
                    whatsapp: confirmedContacts.whatsapp?.trim() || null,
                    viber: confirmedContacts.viber?.trim() || null,
                    twitter: null, 
                }
            }
        };
        update(payload)
    };

    return {
        open,
        isChanged,
        isLoading,
        filledContacts,
        profileContacts,
        dialogComponent,
        register,
        onOpenContactDialog,
        onCloseContactDialog,
        handleDeleteContact,
        handleSubmitContacts,
    };
};

export default useProfileContacts;
